// OHGrid — Composant grille réutilisable conforme GROOT_RULES v1.1.1
// UI-005: Tri newest-first par défaut
// UI-006: Actions par ligne via DropdownMenu
// UI-007: Tri cliquable sur colonnes avec indicateur
// UI-002: Sélection multiple + actions groupées
// UI-004: Stats cards CLIQUABLES = filtres
import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Search, ArrowUpDown, ArrowUp, ArrowDown,
  MoreHorizontal, RefreshCw, Loader2,
  ChevronLeft, ChevronRight, SlidersHorizontal, X,
} from "lucide-react";
import { Input } from "./input";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

// Palette sémantique : références aux variables CSS définies dans index.css
// (oh-stat-{color}-{bg|text|border|active-bg|active-border}) → cohérence
// dark/light mode automatique. Cf. OH-AUDIT-009 quick win 5.
const _statVars = (color) => ({
  bg:           `var(--oh-stat-${color}-bg)`,
  text:         `var(--oh-stat-${color}-text)`,
  border:       `var(--oh-stat-${color}-border)`,
  activeBg:     `var(--oh-stat-${color}-active-bg)`,
  activeBorder: `var(--oh-stat-${color}-active-border)`,
});

const COLOR_MAP = {
  blue:   _statVars("blue"),
  green:  _statVars("green"),
  red:    _statVars("red"),
  yellow: _statVars("yellow"),
  cyan:   _statVars("cyan"),
  purple: _statVars("purple"),
  orange: _statVars("orange"),
  amber:  _statVars("amber"),
  gray:   { bg: "var(--oh-bg-elevated)", text: "var(--oh-text-muted)", border: "var(--oh-border)", activeBg: "var(--oh-bg-elevated)", activeBorder: "var(--oh-text-muted)" },
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * OHGrid v1.1.1 — Stats cliquables + filtres avancés + pagination
 *
 * @param {Array}    stats              - [{key, label, value, icon, color, filter?(row)=>bool}]
 *                                        Si `filter` est défini, la card est cliquable.
 *                                        Clic = active le filtre. Re-clic = désactive.
 * @param {Array}    advancedFilters    - [{key, label, type:"select"|"checkbox", options?:[{value,label}], defaultValue?}]
 * @param {Function} onAdvancedFilter   - Callback quand un filtre avancé change: ({key, value}) => void
 * @param {Array}    headerActions      - [{label, icon, onClick, className?}] Boutons en haut à droite
 */
export function OHGrid({
  data = [],
  columns = [],
  rowActions = [],
  onRowClick,
  defaultSortKey = "created_at",
  defaultSortDir = "desc",
  searchPlaceholder = "Rechercher...",
  searchFilter,
  selectable = false,
  bulkActions = [],
  loading = false,
  emptyMessage = "Aucun résultat",
  onRefresh,
  testIdPrefix = "ohgrid",
  stats = [],
  pagination = false,
  pageSize: pageSizeProp = 50,
  serverSide = false,
  onPageChange,
  totalCount,
  advancedFilters = [],
  onAdvancedFilter,
  headerActions = [],
  defaultActiveStat = null,
  onActiveStatChange,
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    PAGE_SIZE_OPTIONS.includes(pageSizeProp) ? pageSizeProp : 50
  );
  const [activeStatKey, setActiveStatKey] = useState(defaultActiveStat);
  // 🔴 N°940 · D — filtres PAR COLONNE (conserve les filtres du catalogue Mario lors de la
  //    migration vers OHGrid). Une colonne les déclare via `col.filter` ({type:"text"|"select",
  //    options?, placeholder?, value?(row)}). Rétrocompatible : sans aucun `col.filter`, ⛔ aucune
  //    ligne de filtres, comportement des 4 autres grilles INCHANGÉ.
  const [colFilters, setColFilters] = useState({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advFilterValues, setAdvFilterValues] = useState(() => {
    const defaults = {};
    advancedFilters.forEach(f => { defaults[f.key] = f.defaultValue ?? (f.type === "checkbox" ? false : "all"); });
    return defaults;
  });

  // Stat filter
  const activeStat = stats.find(s => s.key === activeStatKey);
  const statFilterFn = activeStat?.filter || null;

  // 🔴 N°940 · D — valeur filtrable d'une colonne pour une ligne (accessor explicite ou brut).
  const valeurColonne = (col, row) => {
    const v = col.filter?.value ? col.filter.value(row) : row[col.key];
    return v == null ? "" : String(v);
  };
  const colFiltersActifs = columns.some(
    c => c.filter && colFilters[c.key] != null && colFilters[c.key] !== "" && colFilters[c.key] !== "all");

  // Search + stat filter + filtres colonne combinés
  const filtered = useMemo(() => {
    let result = data;
    // Apply stat filter
    if (statFilterFn) result = result.filter(statFilterFn);
    // Apply search
    if (search) {
      const q = search.toLowerCase();
      if (searchFilter) result = result.filter(row => searchFilter(row, q));
      else result = result.filter(row =>
        Object.values(row).some(v => typeof v === "string" && v.toLowerCase().includes(q))
      );
    }
    // Apply filtres par colonne
    for (const col of columns) {
      const val = colFilters[col.key];
      if (!col.filter || val == null || val === "" || val === "all") continue;
      if (col.filter.type === "select") {
        result = result.filter(row => valeurColonne(col, row) === String(val));
      } else {
        const q = String(val).toLowerCase();
        result = result.filter(row => valeurColonne(col, row).toLowerCase().includes(q));
      }
    }
    return result;
  }, [data, search, searchFilter, statFilterFn, colFilters, columns]);

  // Sort
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const va = (a[sortKey] ?? "").toString().toLowerCase();
    const vb = (b[sortKey] ?? "").toString().toLowerCase();
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sortDir === "asc" ? cmp : -cmp;
  }), [filtered, sortKey, sortDir]);

  // Pagination
  const totalItems = serverSide ? (totalCount ?? data.length) : sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const paginated = useMemo(() => {
    if (!pagination || serverSide) return sorted;
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, pagination, serverSide, page, pageSize]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    if (serverSide && onPageChange) onPageChange({ page: newPage, pageSize });
  }, [serverSide, onPageChange, pageSize]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
    if (serverSide && onPageChange) onPageChange({ page: 1, pageSize: newSize });
  }, [serverSide, onPageChange]);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  // SAV #753 — un zéro doit se DIRE, et dire POURQUOI. « Aucun dossier trouvé »
  // ne distingue pas « la grille est vide » de « ta recherche ne correspond à
  // rien » : c'est la différence entre une panne et une contre-épreuve réussie.
  // Une seule source pour les deux rendus (carte mobile et tableau desktop).
  const emptyText = search
    ? `Aucun résultat pour « ${search} »`
    : emptyMessage;

  const handleStatClick = (stat) => {
    if (!stat.filter) return; // Not clickable if no filter fn
    const next = activeStatKey === stat.key ? null : stat.key;
    setActiveStatKey(next);
    onActiveStatChange?.(next);
    setPage(1);
  };

  const handleAdvFilterChange = (key, value) => {
    setAdvFilterValues(prev => ({ ...prev, [key]: value }));
    onAdvancedFilter?.({ key, value });
    setPage(1);
  };

  const activeAdvFilterCount = Object.entries(advFilterValues).filter(([k, v]) => {
    const def = advancedFilters.find(f => f.key === k);
    if (!def) return false;
    if (def.type === "checkbox") return v === true;
    return v !== "all" && v !== "" && v !== def.defaultValue;
  }).length;

  const resetAdvFilters = () => {
    const defaults = {};
    advancedFilters.forEach(f => { defaults[f.key] = f.defaultValue ?? (f.type === "checkbox" ? false : "all"); });
    setAdvFilterValues(defaults);
    advancedFilters.forEach(f => onAdvancedFilter?.({ key: f.key, value: defaults[f.key] }));
  };

  const toggleSort = useCallback((key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  }, [sortKey]);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-cyan-400" /> : <ArrowDown className="w-3 h-3 text-cyan-400" />;
  };

  // 🔴 P-MARIO-04 — la sélection ⛔ survit pas aux lignes disparues. Quand `data` change
  // (suppression, rechargement), les identifiants ABSENTS sont retirés de la sélection :
  // sinon « N sélectionnés » compte des lignes fantômes, et une action de masse les
  // renverrait au serveur. ⚠️ Mode `serverSide` exclu (data = une page seulement).
  useEffect(() => {
    if (serverSide) return;
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const presents = new Set(data.map(d => d.id));
      const next = new Set([...prev].filter(id => presents.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [data, serverSide]);

  const allSelected = paginated.length > 0 && paginated.every(d => selectedIds.has(d.id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(paginated.map(d => d.id)));
  };
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const colSpan = columns.length + (selectable ? 1 : 0) + (rowActions.length > 0 ? 1 : 0);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-container`}>

      {/* Stats cards CLIQUABLES (UI-004) — responsive breakpoints :
            mobile  : max 2 colonnes (évite débordement texte/montants)
            tablet  : 3-4 colonnes selon nombre de stats
            desktop : 1 colonne par stat, capé à 6 (au-delà → wrap) */}
      {stats.length > 0 && (
        <div
          className={`grid gap-3 ${
            stats.length === 1 ? "grid-cols-1" :
            stats.length === 2 ? "grid-cols-2" :
            stats.length === 3 ? "grid-cols-1 sm:grid-cols-3" :
            stats.length === 4 ? "grid-cols-2 sm:grid-cols-4" :
            stats.length === 5 ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" :
            "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
          }`}
        >
          {stats.map((stat, i) => {
            const palette = COLOR_MAP[stat.color] || COLOR_MAP.gray;
            const Icon = stat.icon;
            const isActive = activeStatKey === stat.key;
            const isClickable = !!stat.filter;
            return (
              <div
                key={stat.key || i}
                data-testid={`${testIdPrefix}-stat-${stat.key || i}`}
                onClick={() => isClickable && handleStatClick(stat)}
                className={`min-w-0 rounded-lg px-4 py-3 flex items-center gap-3 transition-all ${isClickable ? "cursor-pointer hover:scale-[1.02]" : ""} ${isActive ? "ring-1" : ""}`}
                style={{
                  background: isActive ? palette.activeBg : palette.bg,
                  border: `1px solid ${isActive ? palette.activeBorder : palette.border}`,
                  ringColor: isActive ? palette.activeBorder : "transparent",
                }}
              >
                {Icon && (
                  <div className="shrink-0 p-2 rounded-md" style={{ background: isActive ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)" }}>
                    <Icon className="w-4 h-4" style={{ color: palette.text }} />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-[11px] truncate" style={{ color: isActive ? palette.text : "var(--oh-text-muted)" }}>{stat.label}</p>
                  <p className="text-xl font-medium leading-tight" style={{ color: palette.text }}>
                    {stat.value ?? "—"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Search bar + filters + bulk */}
      {/* SAV #753 — sur mobile la barre était UNE seule ligne flex sans `flex-wrap` :
          la recherche (`flex-1`) était écrasée par « Filtres », le compteur, les
          headerActions (« Ajouter un dossier », « Export ») et le rafraîchissement.
          Sur 375 px il ne restait que quelques pixels de zone de saisie — d'où
          « je ne vois pas ce que je tape ». La barre s'enroule désormais, et la
          recherche prend TOUTE la largeur sur mobile. ⛔ Rien ne change ≥ 768 px. */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Bulk actions OR search bar */}
          {selectable && selectedIds.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto md:flex-1 px-3 py-1.5 rounded-lg" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
              <span className="text-xs font-medium whitespace-nowrap" style={{ color: "var(--oh-text-muted)" }}>
                {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}
              </span>
              {bulkActions.map((action, i) => (
                <Button key={i} size="sm" variant="outline" className={`h-7 text-xs ${action.className || ""}`}
                  onClick={() => action.onClick([...selectedIds])} data-testid={`${testIdPrefix}-bulk-${i}`}>
                  {action.icon && <action.icon className="w-3.5 h-3.5 mr-1" />}{" "}
                  {/* P-MARIO-04 — libellé fonction du nombre sélectionné (« Supprimer (N) ») ; chaîne inchangée. */}
                  {typeof action.label === "function" ? action.label(selectedIds.size) : action.label}
                </Button>
              ))}
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedIds(new Set())} style={{ color: "var(--oh-text-muted)" }}>
                Annuler
              </Button>
            </div>
          ) : (
            <div className="relative w-full md:w-auto md:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
              <Input
                data-testid={`${testIdPrefix}-search`}
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => handleSearch(e.target.value)}
                type="search"
                inputMode="search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-label={searchPlaceholder}
                /* SAV #753 — h-11 (44 px, cible tactile) et text-base (16 px) sur
                   mobile : ⛔ en dessous de 16 px iOS zoome tout seul et casse la
                   mise en page. Desktop inchangé (h-9 / text-sm ≥ 768 px). */
                className="pl-10 h-11 text-base md:h-9 md:text-sm"
                style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
              />
            </div>
          )}

          {/* Advanced filters toggle */}
          {advancedFilters.length > 0 && (
            <Button
              size="sm" variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`h-9 text-xs gap-1.5 ${activeAdvFilterCount > 0 ? "border-cyan-500/50 text-cyan-400" : ""}`}
              data-testid={`${testIdPrefix}-adv-filters-toggle`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filtres
              {activeAdvFilterCount > 0 && (
                <span className="ml-1 bg-cyan-500/20 text-cyan-400 text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">{activeAdvFilterCount}</span>
              )}
            </Button>
          )}

          {/* Result count */}
          {/* SAV #753 — pendant une recherche, le compte devient une PHRASE lisible
              (« 3 résultats sur 262 »), en couleur de texte pleine et non plus en
              gris atténué : c'est ce qui permet de voir que la saisie a produit
              quelque chose — ou zéro. Hors recherche, l'affichage compact est
              conservé. */}
          <span
            className={`whitespace-nowrap ${search ? "text-sm font-medium md:text-xs" : "text-xs"}`}
            style={{ color: search ? "var(--oh-text)" : "var(--oh-text-muted)" }}
            data-testid={`${testIdPrefix}-count`}
          >
            {search
              ? `${sorted.length} résultat${sorted.length > 1 ? "s" : ""} sur ${data.length}`
              : `${sorted.length}${activeStatKey ? ` / ${data.length}` : ""}`}
          </span>

          {/* Header actions + refresh */}
          {headerActions.map((action, i) => (
            <Button key={i} size="sm" variant="outline" onClick={action.onClick} className={`h-9 text-xs ${action.className || ""}`} data-testid={`${testIdPrefix}-action-${i}`}>
              {action.icon && <action.icon className="w-3.5 h-3.5 mr-1.5" />} {action.label}
            </Button>
          ))}
          {onRefresh && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              disabled={loading}
              data-testid={`${testIdPrefix}-refresh`}
              className="h-9 w-9 p-0"
              aria-label="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>

        {/* Advanced filters panel */}
        {showAdvancedFilters && advancedFilters.length > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid={`${testIdPrefix}-adv-filters-panel`}>
            {advancedFilters.map(f => {
              if (f.type === "checkbox") {
                return (
                  <label key={f.key} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--oh-text-muted)" }}>
                    <Checkbox checked={!!advFilterValues[f.key]} onCheckedChange={(v) => handleAdvFilterChange(f.key, !!v)} data-testid={`${testIdPrefix}-filter-${f.key}`} />
                    {f.label}
                  </label>
                );
              }
              // 🔑 #557 — LE TRI-ÉTAT. Un checkbox ne peut structurellement dire
              // que « Oui » : décoché veut dire « pas de filtre », jamais « Non ».
              // Or ce qu'on cherche, c'est ce qui MANQUE — facture non payée,
              // visite non payée, case de suivi non cochée.
              // ⛔ « Non » et « Non renseigné » ne sont PAS fondus : un dossier
              // dont la facture n'est pas payée et un dossier qui n'a pas de
              // facture ne sont pas le même cas.
              // ⚠️ `type: "tribool"` n'est légitime que si la donnée porte
              // vraiment trois états jusqu'ici. Pour un champ de PRÉSENCE
              // (« y a-t-il des photos ? »), il n'y a pas d'indéterminé : ces
              // filtres restent des checkbox, et c'est honnête.
              if (f.type === "tribool") {
                return (
                  <Select key={f.key} value={advFilterValues[f.key] || "all"} onValueChange={(v) => handleAdvFilterChange(f.key, v)}>
                    <SelectTrigger className="h-8 w-full max-w-[190px] text-xs" style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} data-testid={`${testIdPrefix}-filter-${f.key}`}>
                      <SelectValue placeholder={f.label} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous — {f.label}</SelectItem>
                      <SelectItem value="oui">{f.label} : oui</SelectItem>
                      <SelectItem value="non">{f.label} : non</SelectItem>
                      <SelectItem value="inconnu">{f.label} : non renseigné</SelectItem>
                    </SelectContent>
                  </Select>
                );
              }
              if (f.type === "select" && f.options) {
                return (
                  <Select key={f.key} value={advFilterValues[f.key] || "all"} onValueChange={(v) => handleAdvFilterChange(f.key, v)}>
                    <SelectTrigger className="h-8 w-full max-w-[160px] text-xs" style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} data-testid={`${testIdPrefix}-filter-${f.key}`}>
                      <SelectValue placeholder={f.label} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous — {f.label}</SelectItem>
                      {f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              }
              return null;
            })}
            {activeAdvFilterCount > 0 && (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-[var(--oh-error-text)]" onClick={resetAdvFilters}>
                <X className="w-3 h-3 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Table — desktop (≥md) : tableau classique
          mobile (<md) : cartes empilées (rendues juste après) */}
      <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--oh-border)" }}>
        {/* Mobile : liste de cartes */}
        <div className="block md:hidden divide-y" style={{ borderColor: "var(--oh-border)" }}>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: "var(--oh-text-muted)" }} />
              <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement...</p>
            </div>
          ) : paginated.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm" data-testid={`${testIdPrefix}-empty`} style={{ color: "var(--oh-text-muted)" }}>{emptyText}</p>
            </div>
          ) : paginated.map((row, i) => {
            const isSelected = selectedIds.has(row.id);
            const visibleActions = rowActions.filter(a => !a.show || a.show(row));
            const [primaryCol, ...secondaryCols] = columns;
            return (
              <div
                key={row.id || i}
                className={`p-3 ${onRowClick ? "cursor-pointer" : ""} ${isSelected ? "bg-cyan-500/5" : ""}`}
                onClick={() => onRowClick?.(row)}
                data-testid={`${testIdPrefix}-row-${row.id || i}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  {selectable && (
                    <span onClick={e => e.stopPropagation()} className="pt-0.5">
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(row.id)} />
                    </span>
                  )}
                  {primaryCol && (
                    <div className="flex-1 min-w-0 font-medium text-sm" style={{ color: "var(--oh-text)" }}>
                      {primaryCol.render ? primaryCol.render(row[primaryCol.key], row) : (row[primaryCol.key] ?? "—")}
                    </div>
                  )}
                  {visibleActions.length > 0 && (
                    <span onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0"
                            data-testid={`${testIdPrefix}-menu-${row.id || i}`}
                            aria-label="Plus d'actions"
                          >
                            <MoreHorizontal className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {visibleActions.map((action, j) => (
                            <DropdownMenuItem key={j} onClick={() => action.onClick(row)} className={action.className || ""}>
                              {action.icon && <action.icon className="w-4 h-4 mr-2" />} {action.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  )}
                </div>
                {secondaryCols.length > 0 && (
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
                    {secondaryCols.map(col => (
                      <React.Fragment key={col.key}>
                        <dt className="uppercase tracking-wider" style={{ color: "var(--oh-text-muted)" }}>
                          {col.label}
                        </dt>
                        <dd className="text-right break-words" style={{ color: "var(--oh-text)" }}>
                          {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop : table classique */}
        <div className="hidden md:block overflow-x-auto oh-responsive-table">
          <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr style={{ background: "var(--oh-bg-elevated)" }}>
                {selectable && (
                  <th className="px-3 py-3 w-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid={`${testIdPrefix}-select-all`} />
                  </th>
                )}
                {columns.map(col => (
                  <th
                    key={col.key}
                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${col.sortable !== false ? "cursor-pointer select-none hover:opacity-80" : ""} ${col.className || ""}`}
                    style={{ color: sortKey === col.key ? "var(--oh-text)" : "var(--oh-text-muted)" }}
                    onClick={() => col.sortable !== false && toggleSort(col.key)}
                    data-testid={`${testIdPrefix}-col-${col.key}`}
                  >
                    <span className="flex items-center gap-1.5">
                      {col.label}
                      {col.sortable !== false && <SortIcon col={col.key} />}
                    </span>
                  </th>
                ))}
                {rowActions.length > 0 && (
                  <th className="px-3 py-3 w-12 text-right text-xs font-medium uppercase tracking-wider" style={{ color: "var(--oh-text-muted)" }}>
                    Actions
                  </th>
                )}
              </tr>
              {/* 🔴 N°940 · D — ligne de filtres PAR COLONNE (seulement si déclarés). */}
              {columns.some(c => c.filter) && (
                <tr style={{ borderTop: "1px solid var(--oh-border)", background: "var(--oh-bg-surface)" }}>
                  {selectable && <th className="px-3 py-2 w-10" />}
                  {columns.map(col => (
                    <th key={col.key} className="px-4 py-2">
                      {col.filter && col.filter.type === "select" ? (
                        <select
                          data-testid={`${testIdPrefix}-filter-${col.key}`}
                          value={colFilters[col.key] ?? "all"}
                          onChange={e => { setColFilters(f => ({ ...f, [col.key]: e.target.value })); setPage(1); }}
                          className="h-7 text-xs rounded-md w-full"
                          style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                                   color: "var(--oh-text)" }}>
                          <option value="all">{col.filter.placeholder || "Tous"}</option>
                          {(col.filter.options || []).map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : col.filter ? (
                        <input
                          data-testid={`${testIdPrefix}-filter-${col.key}`}
                          placeholder={col.filter.placeholder || "Filtrer…"}
                          value={colFilters[col.key] ?? ""}
                          onChange={e => { setColFilters(f => ({ ...f, [col.key]: e.target.value })); setPage(1); }}
                          className="h-7 text-xs rounded-md w-full px-2"
                          style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                                   color: "var(--oh-text)" }}
                        />
                      ) : null}
                    </th>
                  ))}
                  {rowActions.length > 0 && (
                    <th className="px-3 py-2 text-right">
                      {colFiltersActifs && (
                        <button
                          type="button"
                          data-testid={`${testIdPrefix}-filter-clear`}
                          onClick={() => { setColFilters({}); setPage(1); }}
                          className="h-7 text-xs px-2 rounded-md"
                          style={{ color: "var(--oh-text-muted)" }}>
                          Effacer
                        </button>
                      )}
                    </th>
                  )}
                </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colSpan} className="text-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: "var(--oh-text-muted)" }} />
                  <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement...</p>
                </td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={colSpan} className="text-center py-12">
                  <p className="text-sm" data-testid={`${testIdPrefix}-empty`} style={{ color: "var(--oh-text-muted)" }}>{emptyText}</p>
                </td></tr>
              ) : paginated.map((row, i) => {
                const isSelected = selectedIds.has(row.id);
                const visibleActions = rowActions.filter(a => !a.show || a.show(row));
                return (
                  <tr
                    key={row.id || i}
                    className={`transition-colors ${onRowClick ? "cursor-pointer" : ""} ${isSelected ? "bg-cyan-500/5" : ""}`}
                    style={{ borderTop: "1px solid var(--oh-border)" }}
                    onClick={() => onRowClick?.(row)}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "var(--oh-bg-elevated)"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ""; }}
                    data-testid={`${testIdPrefix}-row-${row.id || i}`}
                  >
                    {selectable && (
                      <td className="px-3 py-3 w-10" onClick={e => e.stopPropagation()}>
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(row.id)} />
                      </td>
                    )}
                    {columns.map(col => (
                      <td key={col.key} className={`px-4 py-3 ${col.className || ""}`} style={{ color: "var(--oh-text)" }}>
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                      </td>
                    ))}
                    {rowActions.length > 0 && (
                      <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                        {visibleActions.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-10 w-10 p-0 md:h-7 md:w-7"
                                data-testid={`${testIdPrefix}-menu-${row.id || i}`}
                                aria-label="Plus d'actions"
                              >
                                <MoreHorizontal className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {visibleActions.map((action, j) => (
                                <DropdownMenuItem key={j} onClick={() => action.onClick(row)} className={action.className || ""}>
                                  {action.icon && <action.icon className="w-4 h-4 mr-2" />} {action.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: count + pagination */}
      <div className="flex items-center justify-between gap-3 px-1">
        <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
          {pagination
            ? `${Math.min((page - 1) * pageSize + 1, totalItems)}–${Math.min(page * pageSize, totalItems)} sur ${totalItems}`
            : `${sorted.length} résultat${sorted.length > 1 ? "s" : ""}${search ? ` sur ${data.length}` : ""}`
          }
        </p>

        {pagination && (
          <div className="flex items-center gap-2">
            <select
              data-testid={`${testIdPrefix}-pagination-pagesize`}
              value={pageSize}
              onChange={e => handlePageSizeChange(Number(e.target.value))}
              className="text-xs rounded px-2 py-1 h-7"
              style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
            >
              {PAGE_SIZE_OPTIONS.map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
            <Button size="sm" variant="outline" className="h-10 w-10 p-0 md:h-7 md:w-7"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
              data-testid={`${testIdPrefix}-pagination-prev`}
              aria-label="Page précédente"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs tabular-nums" style={{ color: "var(--oh-text-muted)" }}>
              {page} / {totalPages}
            </span>
            <Button size="sm" variant="outline" className="h-10 w-10 p-0 md:h-7 md:w-7"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
              data-testid={`${testIdPrefix}-pagination-next`}
              aria-label="Page suivante"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
